import { useState, useEffect, useCallback, forwardRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, Loader2 } from 'lucide-react';

import { DepositStep, DepositStatus, DepositModalProps } from './types';
import { StepIndicator } from './StepIndicator';
import { AmountStep } from './AmountStep';
import { PaymentStep } from './PaymentStep';
import { WaitingStep } from './WaitingStep';
import { SuccessStep } from './SuccessStep';
import { FailedStep } from './FailedStep';
import { DepositUnavailable } from './DepositUnavailable';

const MIN_DEPOSIT = 1;
const MAX_DEPOSIT = 100;

export const DepositModal = forwardRef<HTMLDivElement, DepositModalProps>(function DepositModal({ open, onOpenChange }, ref) {
  const { user } = useAuth();
  const [step, setStep] = useState<DepositStep>('amount');
  const [amount, setAmount] = useState('');
  const [depositWallet, setDepositWallet] = useState('');
  const [isLoadingWallet, setIsLoadingWallet] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingDepositId, setPendingDepositId] = useState<string | null>(null);
  const [depositStatus, setDepositStatus] = useState<DepositStatus>('pending');
  const [confirmedCredits, setConfirmedCredits] = useState(0);

  const amountNum = parseFloat(amount) || 0;
  const creditsToReceive = Math.floor(amountNum * 100);

  // Fetch wallet address on open
  useEffect(() => {
    const fetchWallet = async () => {
      setIsLoadingWallet(true);
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'deposit_wallet')
        .maybeSingle();
      
      if (data?.value) {
        setDepositWallet((data.value as { address?: string }).address || '');
      }
      setIsLoadingWallet(false);
    };

    if (open) {
      fetchWallet();
      // Reset state when opening
      setStep('amount');
      setAmount('');
      setPendingDepositId(null);
      setDepositStatus('pending');
      setConfirmedCredits(0);
    }
  }, [open]);

  // Poll for deposit status when waiting
  useEffect(() => {
    if ((step !== 'waiting') || !pendingDepositId) return;

    const checkStatus = async () => {
      const { data } = await supabase
        .from('deposit_transactions')
        .select('status, credits_added')
        .eq('id', pendingDepositId)
        .single();

      if (data) {
        if (data.status === 'confirmed') {
          setDepositStatus('confirmed');
          setConfirmedCredits(data.credits_added);
          setStep('success');
          toast.success('Deposit confirmed! Credits added to your account.');
        } else if (data.status === 'rejected' || data.status === 'expired') {
          setDepositStatus('rejected');
          setStep('failed');
        } else if (data.status === 'detecting') {
          setDepositStatus('detecting');
        }
      }
    };

    // Check immediately
    checkStatus();
    
    // Then poll every 5 seconds
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [step, pendingDepositId]);

  const handleProceedToPayment = useCallback(() => {
    if (amountNum < MIN_DEPOSIT || amountNum > MAX_DEPOSIT) {
      toast.error(`Amount must be between $${MIN_DEPOSIT} and $${MAX_DEPOSIT}`);
      return;
    }
    setStep('payment');
  }, [amountNum]);

  const handleConfirmPayment = useCallback(async () => {
    if (!user) {
      toast.error('Please sign in to deposit');
      return;
    }

    if (!depositWallet) {
      toast.error('Deposit wallet not available');
      return;
    }

    setIsSubmitting(true);

    const { data, error } = await supabase
      .from('deposit_transactions')
      .insert({
        user_id: user.id,
        amount_usd: amountNum,
        credits_added: creditsToReceive,
        wallet_address: depositWallet,
        status: 'pending',
      })
      .select('id')
      .single();

    setIsSubmitting(false);

    if (error) {
      console.error('Deposit error:', error);
      toast.error('Failed to create deposit request');
      return;
    }

    setPendingDepositId(data.id);
    setDepositStatus('pending');
    setStep('waiting');
    toast.success('Deposit submitted! Monitoring blockchain for your transaction.');
  }, [user, depositWallet, amountNum, creditsToReceive]);

  const handleSubmitTxHash = useCallback(async (txHash: string) => {
    if (!pendingDepositId) return;

    const { error } = await supabase
      .from('deposit_transactions')
      .update({ tx_hash: txHash })
      .eq('id', pendingDepositId);

    if (error) {
      toast.error('Failed to submit transaction hash');
    } else {
      toast.success('Transaction hash submitted for support review');
    }
  }, [pendingDepositId]);

  const handleRetry = useCallback(() => {
    setStep('amount');
    setAmount('');
    setPendingDepositId(null);
    setDepositStatus('pending');
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const getDialogTitle = () => {
    switch (step) {
      case 'success':
        return 'Deposit Complete';
      case 'failed':
        return 'Deposit Failed';
      default:
        return 'Deposit Credits';
    }
  };

  const getDialogDescription = () => {
    switch (step) {
      case 'amount':
        return 'Send USDT (TRC20) to receive credits. 1 USDT = 100 credits.';
      case 'payment':
        return 'Send the exact amount to complete your deposit.';
      case 'waiting':
        return 'Your transaction is being verified on the blockchain.';
      case 'success':
        return 'Your credits have been added to your account.';
      case 'failed':
        return 'There was an issue with your deposit.';
      default:
        return '';
    }
  };

  // Show loading state
  if (isLoadingWallet) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Deposit Credits
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show unavailable state
  if (!depositWallet) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Deposit Credits
            </DialogTitle>
          </DialogHeader>
          <DepositUnavailable />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator - only show for main steps */}
        {(step === 'amount' || step === 'payment' || step === 'waiting') && (
          <StepIndicator currentStep={step} />
        )}

        {/* Step Content */}
        {step === 'amount' && (
          <AmountStep
            amount={amount}
            onAmountChange={setAmount}
            onContinue={handleProceedToPayment}
            minDeposit={MIN_DEPOSIT}
            maxDeposit={MAX_DEPOSIT}
          />
        )}

        {step === 'payment' && (
          <PaymentStep
            amount={amountNum}
            credits={creditsToReceive}
            walletAddress={depositWallet}
            isSubmitting={isSubmitting}
            onBack={() => setStep('amount')}
            onConfirmPayment={handleConfirmPayment}
          />
        )}

        {step === 'waiting' && (
          <WaitingStep
            amount={amountNum}
            credits={creditsToReceive}
            status={depositStatus}
            onSubmitTxHash={handleSubmitTxHash}
          />
        )}

        {step === 'success' && (
          <SuccessStep
            amount={amountNum}
            credits={confirmedCredits || creditsToReceive}
            onClose={handleClose}
          />
        )}

        {step === 'failed' && (
          <FailedStep
            amount={amountNum}
            onRetry={handleRetry}
            onSubmitTxHash={handleSubmitTxHash}
          />
        )}
      </DialogContent>
    </Dialog>
  );
});

DepositModal.displayName = 'DepositModal';
